/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/entity/template
 * @desc Column Definition for entity template
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import EntityActionUpdateTemplate from 'labstep-web/components/Entity/Action/UpdateTemplate';
import { FilterTemplate } from 'labstep-web/components/Filter/Template';
import type { FilterTemplateProps } from 'labstep-web/components/Filter/Template/types';
import { ICONS } from 'labstep-web/constants/icons';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  getEditable,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Device } from 'labstep-web/models/device.model';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Resource } from 'labstep-web/models/resource.model';

export const getColDefEntityTemplate = (
  entityName: string,
): SmartTableColDef => ({
  colId: 'template',
  headerName: 'Category',
  headerComponentParams: {
    icon: ICONS.resource_template.primary,
    filter: (props: FilterTemplateProps) => (
      <FilterTemplate
        {...props}
        entityName={entityName as 'device' | 'resource'}
      />
    ),
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolConditionService.getEditable(params),
  valueGetter: (params: ValueGetterParams) =>
    params.data?.template?.idAttr || null,
  valueGetterExport: (params: ValueGetterParams) =>
    params.node?.data?.template?.name || 'Untitled Category',
  valueSetter: (params: ValueSetterParams) => {
    return GridSmartTableProtocolConditionService.updateEntity(
      params,
      params.data?.entityName,
      params.data?.idAttr,
      {
        template_id: params.newValue,
      },
    );
  },
  cellRenderer: (params: CellRendererParams<Entity>) => {
    const entity = params.data?.template;
    if (!entity) {
      return (
        <DataGridPlaceholder
          params={params}
          editable={getEditable()}
          children="Specify category"
        />
      );
    }
    return null;
  },
  cellEditorPopup: true,
  cellEditor: (params: CellEditorParams<Entity>) => {
    return (
      <ParamsStateContextProvider value={{}}>
        <EntityActionUpdateTemplate
          entity={params.data as Resource | Device}
          options={{ onSuccess: () => stopEditingAndFocus(params) }}
          modalProps={{
            open: true,
            onClose: () => stopEditingAndFocus(params),
            viewComponent: null,
          }}
        />
      </ParamsStateContextProvider>
    );
  },
});
